// The storage limit levels and what the web does at each (product owner,
// 2026-11-03). Nothing is ever refused: phones keep uploading at any level.
//
//   95%   heads-up email on Mondays (server only)
//   100%  daily email; banner on the task page, for everyone
//   105%  pop-up on the task page for workspace admins: on page load and
//         when a new photo arrives, at most once every 5 minutes
//   110%  daily email; photos hidden on the task pages for workspace users
//         (photos stay saved and downloads still show them; system admins
//         and the customer portal keep seeing them)
//
// Mirrors `api/app/core/storage_limit.py` and
// `functions/functions/storage_notices.py`; LEVEL_CASES in each one's tests
// keeps them in step. Used storage is `storage_used` on the workspace
// document; the limit is plan plus extra storage bought, never the stale
// `allocated_storage`.
import { PLAN_STORAGE, normalisePlan } from '../constants/plans';

export const HEADS_UP_PERCENT = 95;
export const FULL_PERCENT = 100;
export const POPUP_PERCENT = 105;
export const PHOTOS_HIDDEN_PERCENT = 110;

export const LEVEL = {
  OK: 'ok',
  FILLING: 'filling',
  FULL: 'full',
  OVER: 'over',
  PHOTOS_HIDDEN: 'photos_hidden',
};

/** How long the pop-up stays away once shown (per browser and workspace). */
export const POPUP_INTERVAL_MS = 5 * 60 * 1000;

/** A task new to the list counts as "a new photo" only if it arrived this recently. */
const ARRIVAL_WINDOW_MS = 5 * 60 * 1000;

const GB = 1024 * 1024 * 1024;

export function storageUsedBytes(data) {
  const used = data?.storage_used ?? data?.storageUsed;
  const n = Number(used);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function storageLimitBytes(data) {
  return PLAN_STORAGE[normalisePlan(data?.plan)] + (Number(data?.additional_storage) || 0);
}

/** Whole-number thresholds, compared without rounding: 99.99% is not full. */
export function storageLevel(usedBytes, limitBytes) {
  if (!limitBytes || limitBytes <= 0) return LEVEL.OK;
  const scaled = usedBytes * 100;
  if (scaled >= limitBytes * PHOTOS_HIDDEN_PERCENT) return LEVEL.PHOTOS_HIDDEN;
  if (scaled >= limitBytes * POPUP_PERCENT) return LEVEL.OVER;
  if (scaled >= limitBytes * FULL_PERCENT) return LEVEL.FULL;
  if (scaled >= limitBytes * HEADS_UP_PERCENT) return LEVEL.FILLING;
  return LEVEL.OK;
}

const atLeast = (level, floor) => {
  const order = [LEVEL.OK, LEVEL.FILLING, LEVEL.FULL, LEVEL.OVER, LEVEL.PHOTOS_HIDDEN];
  return order.indexOf(level) >= order.indexOf(floor);
};

/**
 * What the task pages do for this viewer. `subscriberData` is the workspace
 * document (useFeatureAccess). System admins and customer sessions always
 * see photos; only a workspace admin is shown the pop-up.
 */
export function storageLimitState(subscriberData, { isSystemAdmin = false, userRole = null } = {}) {
  const usedBytes = storageUsedBytes(subscriberData);
  const limitBytes = storageLimitBytes(subscriberData);
  const level = subscriberData ? storageLevel(usedBytes, limitBytes) : LEVEL.OK;
  const workspaceUser = !isSystemAdmin && userRole !== 'customer';
  return {
    level,
    usedBytes,
    limitBytes,
    // Rounded down, as in the emails: 99.7% is not "100%".
    percent: limitBytes ? Math.floor((usedBytes * 100) / limitBytes) : 0,
    photosHiddenAtBytes: Math.floor((limitBytes * PHOTOS_HIDDEN_PERCENT) / 100),
    showBanner: atLeast(level, LEVEL.FULL),
    showPopup: workspaceUser && userRole === 'admin' && atLeast(level, LEVEL.OVER),
    photosHidden: workspaceUser && level === LEVEL.PHOTOS_HIDDEN,
  };
}

export const formatGb = (bytes) => `${Math.round((bytes || 0) / GB)} GB`;

const hasUrl = (img) =>
  Boolean(img?.thumbUrl || img?.thumbnailUrl || img?.mediumUrl || img?.fullUrl || img?.url || img?.videoUrl);

/** Photos of a task that have arrived: the higher of the phone's counter and the photos with a link. */
export function arrivedPhotoCount(task) {
  const images = Array.isArray(task?.images) ? task.images : [];
  return Math.max(Number(task?.imagesUploadedCount) || 0, images.filter(hasUrl).length);
}

const millis = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  const t = new Date(ts).getTime();
  return Number.isFinite(t) ? t : 0;
};

/**
 * Whether a new photo arrived between two deliveries of the task list.
 * `seen` maps task id to its arrived photo count from earlier deliveries.
 * A photo arrives when a task already on the list gains one, or when a task
 * new to the list finished or was submitted in the last few minutes. A page
 * or filter change brings older tasks in, and is not an arrival.
 */
export function photosArrived(seen, tasks, now = Date.now()) {
  const next = new Map(seen);
  let arrived = false;
  for (const task of tasks || []) {
    if (!task?.id) continue;
    const count = arrivedPhotoCount(task);
    if (seen.has(task.id)) {
      if (count > seen.get(task.id)) arrived = true;
    } else if (seen.size > 0 && count > 0) {
      const at = Math.max(millis(task.uploadCompletedAt), millis(task.submittedAt));
      if (at && now - at < ARRIVAL_WINDOW_MS) arrived = true;
    }
    next.set(task.id, count);
  }
  return { arrived, seen: next };
}

const popupKey = (subscriberId) => `traklog:storagePopupAt:${subscriberId || 'none'}`;

/** True when the pop-up may show now; records the showing when it may. */
export function claimPopupSlot(subscriberId, now = Date.now()) {
  try {
    const last = Number(window.localStorage.getItem(popupKey(subscriberId))) || 0;
    if (now - last < POPUP_INTERVAL_MS) return false;
    window.localStorage.setItem(popupKey(subscriberId), String(now));
    return true;
  } catch {
    return true;
  }
}
